// Bite 4 — Morning Check helpers. Color is SEMANTIC only (trust state), never decorative.

export const centsToUSD = (cents, signed = false) => {
  const n = Number(cents || 0) / 100;
  const s = Math.abs(n).toLocaleString("en-US", { style: "currency", currency: "USD" });
  if (n < 0) return `-${s}`;
  if (signed && n > 0) return `+${s}`;
  return s;
};

/** Format an APU value according to its unit_of_value. */
export const formatAPUValue = (value, unit, signed = false) => {
  const v = Number(value || 0);
  if (unit === "count") {
    const s = Math.abs(v).toLocaleString("en-US");
    if (v < 0) return `-${s}`;
    if (signed && v > 0) return `+${s}`;
    return s;
  }
  if (unit === "percent_basis_points") {
    const pct = v / 100;
    const s = Math.abs(pct).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "%";
    if (pct < 0) return `-${s}`;
    if (signed && pct > 0) return `+${s}`;
    return s;
  }
  if (unit === "date_iso") {
    // Stored as YYYYMMDD integer
    const str = String(v);
    if (str.length === 8) return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
    return str;
  }
  // Default: USD_cents
  return centsToUSD(v, signed);
};

// Build a fact_type -> human label map from GET /api/fact-types.
export const buildFactLabels = (registry = []) => {
  const m = {};
  (registry || []).forEach((f) => {
    if (f?.name) m[f.name] = f.description || prettyFact(f.name);
  });
  return m;
};

export const prettyFact = (name) =>
  String(name || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const factLabel = (name, labels = {}) => labels[name] || prettyFact(name);

// Trust-state palette (semantic). Returns tailwind-friendly classes built on CSS vars.
const TRUST_TONE = {
  reconciled: "success",
  review: "warning",
  partial: "warning",
  inferred: "muted",
  stale: "destructive",
  disputed: "destructive",
  rejected: "muted",
};
export const trustTone = (state) => TRUST_TONE[state] || "muted";

export const TRUST_LABELS = {
  reconciled: "Fact",
  review: "Claim",
  "partial:reconciled": "Partially verified",
  disputed: "Disagrees",
  stale: "Out of date",
  superseded: "Replaced",
  rejected: "Rejected",
};
export const trustLabel = (s) => TRUST_LABELS[s] || s;

export const EVENT_TYPE_LABELS = {
  promoted_to_reconciled: "Promoted to Fact",
  demoted_to_review: "Demoted to Claim",
  demoted_to_stale: "Marked out of date",
  superseded: "Replaced",
  expected_statement_overdue: "Statement overdue",
  matched: "Matched",
  unmatched: "Awaiting match",
};
export const eventLabel = (s) => EVENT_TYPE_LABELS[s] || prettyFact(s);

export const ERROR_TYPE_LABELS = {
  missing_owner: "Owner not yet assigned",
  cross_foot_mismatch: "Numbers don't add up",
};
export const errorLabel = (s) => ERROR_TYPE_LABELS[s] || prettyFact(s);

export const toneClasses = (tone) =>
  ({
    success: "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.35)]",
    warning: "bg-[hsl(var(--warning)/0.12)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.35)]",
    destructive: "bg-[hsl(var(--destructive)/0.12)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.35)]",
    muted: "bg-muted text-muted-foreground border-border",
    neutral: "bg-muted text-muted-foreground border-border",
  }[tone] || "bg-muted text-muted-foreground border-border");

// Reconciled % thresholds: green >=80, yellow 50-80, red <50.
export const reconciledTone = (pct) => (pct >= 80 ? "success" : pct >= 50 ? "warning" : "destructive");

// Overdue badge: green on-time, yellow 1-14d, red >14d.
export const overdueTone = (days) => (days <= 0 ? "success" : days <= 14 ? "warning" : "destructive");

// PM three-state contact rendering.
export const contactStateView = (state, days) => {
  if (state === "never_contacted")
    return { tone: "neutral", label: "Waiting for first statement", dot: "neutral" };
  if (state === "silent") {
    const tone = days != null && days > 60 ? "destructive" : "warning";
    return { tone, label: `Silent for ${days ?? "?"} days`, dot: tone };
  }
  return { tone: "success", label: `Active — last contact ${days ?? 0} days ago`, dot: "success" };
};

export const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : "—");

export const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};
